import React, { useCallback, useEffect, useState } from "react";
import CardDataStats from "../../components/CardDataStats/CardDataStats.tsx";
import PremiumsChart from "../../components/Charts/PremiumsChart.tsx";
import OfficeService from "../../services/OfficeService/OfficeService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import {
  normalizeDate,
  normalizeSummaryReportProperties,
} from "../../utils/NormalizationUtil.ts";
import moment from "moment/moment";
import CapitalizationUtil from "../../utils/CapitalizationUtil.ts";
import NewContractsChart from "../../components/Charts/NewContractsChart.tsx";

interface SummaryDetails {
  count: string;
  newContracts: string;
  time: string;
}

const SUMMARY_MAPPING_ENUM: Record<keyof SummaryDetails, string> = {
  count: "Брой вноски",
  newContracts: "Нови договори",
  time: "Време",
};

const SUMMARY_ICON_MAPPING_ENUM: Record<keyof SummaryDetails, string> = {
  count: "fa fa-calculator",
  newContracts: "fa-solid fa-file-contract",
  time: "fa fa-time",
};

export interface ChartState {
  series: { name: string; data: number[] }[];
  categories?: string[];
  timePeriods?: string[];
}

export interface YAxisConfig {
  max: number;
  tickAmount: number;
  stepSize: number;
}

interface ChartData {
  premium: string;
  newContracts: string;
  period: string;
}

const CACHE_KEYS = {
  USER_ID: "office_user_id",
  SUMMARY: "office_summary_data",
  PREMIUM_CHART: "office_premium_chart_data",
  CONTRACTS_CHART: "office_contracts_chart_data",
  X_AXIS: "office_x_axis_categories",
};

/**
 * Computes a clean Y-axis config from an array of numeric values.
 *
 * Strategy:
 *  - Round the max up to a "nice" number (nearest power-of-10 step).
 *  - Use 10 ticks so the step labels are round and easy to read.
 *    e.g. max=10 000 → step=1 000 → ticks: 1k, 2k … 10k
 *         max=250   → step=25    → ticks: 25, 50 … 250
 */
const computeYAxisConfig = (values: number[]): YAxisConfig => {
  const rawMax = Math.max(...values, 1); // at least 1 to avoid divide-by-zero
  const TICK_COUNT = 10;

  // Find a power-of-10 magnitude for the step
  const roughStep = rawMax / TICK_COUNT;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const niceStep = Math.ceil(roughStep / magnitude) * magnitude;
  const niceMax = niceStep * TICK_COUNT;

  return { max: niceMax, tickAmount: TICK_COUNT, stepSize: niceStep };
};

const Home: React.FC = () => {
  const [summary, setSummary] = useState<SummaryDetails | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [premiumChartData, setPremiumChartData] = useState<ChartState>(null);
  const [contractsData, setContractsData] = useState<ChartState>(null);
  const [premiumYAxis, setPremiumYAxis] = useState<YAxisConfig | null>(null);
  const [contractsYAxis, setContractsYAxis] = useState<YAxisConfig | null>(
    null
  );
  const [isChartLoading, setIsChartLoading] = useState(true);
  const [xAxisCategories, setXAxisCategories] = useState<string[]>([]);

  const { jwt, userID } = useAuth();
  const date = moment().format("DD/MM/YYYY");

  /**
   * On each login the component mounts fresh. We clear session cache when the
   * stored user ID differs from the current one — this covers the case where
   * two different users share the same browser tab within the same session.
   */
  const checkAndClearCache = useCallback((): boolean => {
    const cachedUserId = sessionStorage.getItem(CACHE_KEYS.USER_ID);
    const isNewUser = !cachedUserId || cachedUserId !== String(userID);

    if (isNewUser) {
      Object.values(CACHE_KEYS).forEach((key) =>
        sessionStorage.removeItem(key)
      );
      if (userID) sessionStorage.setItem(CACHE_KEYS.USER_ID, String(userID));
      return true;
    }

    return false;
  }, [userID]);

  useEffect(() => {
    const getSummary = async () => {
      try {
        setIsLoading(true);

        const cacheCleared = checkAndClearCache();
        const cachedSummary = sessionStorage.getItem(CACHE_KEYS.SUMMARY);

        if (!cacheCleared && cachedSummary) {
          setSummary(JSON.parse(cachedSummary));
        } else {
          const summaryData = normalizeSummaryReportProperties(
            (await OfficeService.getOfficeStatistics(
              jwt,
              "month"
            )) as SummaryDetails
          );
          setSummary(summaryData);
          sessionStorage.setItem(
            CACHE_KEYS.SUMMARY,
            JSON.stringify(summaryData)
          );
        }
      } catch (error) {
        throw new Error("Грешка при зареждане на данни:", error);
      } finally {
        setIsLoading(false);
      }
    };

    getSummary();
  }, [jwt, checkAndClearCache]);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setIsChartLoading(true);

        checkAndClearCache();

        const cachedPremiumChart = sessionStorage.getItem(
          CACHE_KEYS.PREMIUM_CHART
        );
        const cachedContractsChart = sessionStorage.getItem(
          CACHE_KEYS.CONTRACTS_CHART
        );
        const cachedXAxis = sessionStorage.getItem(CACHE_KEYS.X_AXIS);

        if (cachedPremiumChart && cachedContractsChart && cachedXAxis) {
          const parsedPremium: ChartState = JSON.parse(cachedPremiumChart);
          const parsedContracts: ChartState = JSON.parse(cachedContractsChart);
          setPremiumChartData(parsedPremium);
          setContractsData(parsedContracts);
          setXAxisCategories(JSON.parse(cachedXAxis));
          setPremiumYAxis(computeYAxisConfig(parsedPremium.series[0].data));
          setContractsYAxis(computeYAxisConfig(parsedContracts.series[0].data));
        } else {
          const response: ChartData = CapitalizationUtil.convertKeysToCamelCase(
            await OfficeService.getOfficeChartStatistic(jwt, date)
          );

          const filteredData = Object.values(response).filter(
            (value) => typeof value === "object"
          );

          const newXAxisCategories = Array.from({ length: 12 }, (_, index) => {
            return moment()
              .subtract(index + 1, "months")
              .format("MM/YYYY");
          }).reverse();

          const categories = Object.values(filteredData).map((item) => {
            const periods = item.period.split("-");
            if (periods && periods.length > 0) {
              return `${normalizeDate(periods[0].trim())} - ${normalizeDate(
                periods[1].trim()
              )}`;
            }
            return "";
          });

          const premiums = Object.values(filteredData).map((item) =>
            Number(item?.premium)
          );

          const contracts = Object.values(filteredData).map((item) =>
            Number(item?.newContracts)
          );

          const newPremiumChartData: ChartState = {
            series: [{ name: "Премии", data: premiums }],
            categories,
          };

          const newContractsData: ChartState = {
            series: [{ name: "Нови договори", data: contracts }],
            categories,
          };

          setPremiumChartData(newPremiumChartData);
          setContractsData(newContractsData);
          setXAxisCategories(newXAxisCategories);
          setPremiumYAxis(computeYAxisConfig(premiums));
          setContractsYAxis(computeYAxisConfig(contracts));

          sessionStorage.setItem(
            CACHE_KEYS.PREMIUM_CHART,
            JSON.stringify(newPremiumChartData)
          );
          sessionStorage.setItem(
            CACHE_KEYS.CONTRACTS_CHART,
            JSON.stringify(newContractsData)
          );
          sessionStorage.setItem(
            CACHE_KEYS.X_AXIS,
            JSON.stringify(newXAxisCategories)
          );
        }
      } catch (error) {
        setPremiumChartData(null);
        setContractsData(null);
        throw new Error("Грешка при зареждане на диаграмата:", error.message);
      } finally {
        setIsChartLoading(false);
      }
    };

    fetchChartData();
  }, [jwt, checkAndClearCache]);

  const defaultSummary: Partial<SummaryDetails> = {
    count: "0",
    newContracts: "0",
  };

  return (
    <>
      <div className="2xl:gap-7.5 grid grid-cols-1 gap-4 dark:text-white md:grid-cols-2 md:gap-6 xl:grid-cols-4">
        {summary &&
          Object.entries(summary).map(([key, value]) =>
            key !== "time" ? (
              <CardDataStats
                key={key}
                loading={isLoading}
                title={SUMMARY_MAPPING_ENUM[key as keyof SummaryDetails]}
                total={value}
                icon={SUMMARY_ICON_MAPPING_ENUM[key as keyof SummaryDetails]}
              />
            ) : null
          )}
        {!summary &&
          Object.entries(defaultSummary).map(([key, value]) =>
            key !== "time" ? (
              <CardDataStats
                key={key}
                loading={isLoading}
                title={SUMMARY_MAPPING_ENUM[key as keyof SummaryDetails]}
                total={value}
                icon={SUMMARY_ICON_MAPPING_ENUM[key as keyof SummaryDetails]}
              />
            ) : null
          )}
      </div>
      <div className="2xl:mt-7.5 2xl:gap-7.5 mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6">
        <PremiumsChart
          chartData={premiumChartData}
          loading={isChartLoading}
          xAxisCategories={xAxisCategories}
          yAxisConfig={premiumYAxis}
        />
      </div>
      <div className="2xl:mt-7.5 2xl:gap-7.5 mt-4 grid grid-cols-12 gap-4 md:mt-6 md:gap-6">
        <NewContractsChart
          chartData={contractsData}
          loading={isChartLoading}
          xAxisCategories={xAxisCategories}
          yAxisConfig={contractsYAxis}
        />
      </div>
    </>
  );
};

export default Home;
